import { createActions, handleActions } from 'redux-actions';
import { Map } from 'immutable';
import { INVITE_STATUS } from '@meiqia/constants';

const MAX_VISITOR_COUNTER = 500;

function removeVisitorByTrackId(state, trackIds) {
  return state.get('visitorList').filter(item => {
    return !trackIds.includes(item.track_id);
  });
}

const filterVisitor = (list, cacheVisitorInviteStatus) => {
  return list.reduce((visitors, newVisitor) => {
    const index = visitors.findIndex(val => val.id === newVisitor.id);
    if (index >= 0) {
      const oldConv = visitors[index];
      visitors[index] = { ...newVisitor, ...oldConv };
    } else {
      const statusItem = cacheVisitorInviteStatus.find(
        statusItem => statusItem.track_id === newVisitor.track_id && newVisitor.invite_status === undefined
      );
      if (statusItem) {
        visitors.push({ ...newVisitor, invite_status: statusItem.invite_status });
      } else {
        visitors.push(newVisitor);
      }
    }
    return visitors;
  }, []);
};

/**
 * 限制最大访客500显示
 * @param {*} oldVistors
 * @param {*} newVisitors
 */
const dealMaxVisitor = (oldVistors, newVisitors) => {
  const vistors = [...oldVistors, ...newVisitors] ?? [];
  if (vistors.length > MAX_VISITOR_COUNTER) return vistors.slice(-MAX_VISITOR_COUNTER);
  else return vistors;
};

function mergeVisitor(oldVistors, newVisitors, cacheVisitorInviteStatus) {
  return filterVisitor(dealMaxVisitor(oldVistors, newVisitors), cacheVisitorInviteStatus);
}

function updateVisitorByTrackId(state, track_id, cb) {
  if (!track_id) return state;
  const list = state.get('visitorList');
  let index = list.findIndex(item => item.track_id === track_id);
  if (index !== -1) {
    return state.updateIn(['visitorList', index], visitor => cb(visitor));
  }
  return state;
}

function updateVisitorInviteStatus(state, data) {
  const { track_id, invite_status } = data;
  if (!track_id) return state;
  const list = state.get('visitorList');
  const index = list.findIndex(item => item.track_id === track_id);
  if (index !== -1) {
    state.updateIn(['visitorList', index], visitor => ({
      ...visitor,
      invite_status: invite_status
    }));
  }
  // visitor_come 和 update_visitor_invite_status 顺序不定，先做缓存
  // offline 和 visitor_come 做了一个延时更新，这里不能根据访客是否在数据中减少数据更新
  const cacheStatus = state.get('cacheVisitorInviteStatus');
  const indexCachestatus = cacheStatus.findIndex(item => item.track_id === track_id);
  if (indexCachestatus !== -1) {
    return state.updateIn(['cacheVisitorInviteStatus', indexCachestatus], visitor => ({
      ...visitor,
      invite_status: invite_status
    }));
  }

  return state.set('cacheVisitorInviteStatus', [...cacheStatus, data]);
}

function addOrMergeQueue(state, track_id, cb) {
  if (!track_id) return state;
  const list = state.get('queueList');
  let index = list.findIndex(item => item.track_id === track_id);
  if (index !== -1) {
    return state.updateIn(['queueList', index], visitor => cb(visitor));
  }

  return state.set('queueList', [...list, cb({})]);
}

function updateQueueStatus(state, trackIds, cb) {
  const list = state.get('queueList');
  const newList = list.map(val => {
    if (trackIds.includes(val.track_id)) {
      return cb(val);
    }
    return val;
  });
  return state.set('queueList', newList);
}

function removeQueueByTrackId(state, trackIds) {
  return state.set(
    'queueList',
    state.get('queueList').filter(item => {
      return !trackIds.includes(item.track_id);
    })
  );
}

const initState = Map({
  visitorList: [],
  cacheVisitorInviteStatus: [],
  queueList: [],
  queueInline: [],
  onlineCount: 0
});

export const {
  getVisitorCondition,
  getVisitor,
  visitorCome,
  visitorOffline,
  endConversation,
  inviteVisitor,
  rejectInvite,
  acceptInvite,
  changeInviteStatus,
  updateVisitorStatus,
  getQueue,
  getQueueInline,
  addQueue,
  removeQueue,
  queueOffline,
  queueOnline
} = createActions({
  GET_VISITOR: data => data,
  VISITOR_COME: data => data,
  VISITOR_OFFLINE: data => data,
  END_CONVERSATION: data => data,
  INVITE_VISITOR: data => data,
  REJECT_INVITE: data => data,
  ACCEPT_INVITE: data => data,
  INVITE_NO_RESPONSE: data => data,
  CHANGE_INVITE_STATUS: data => data,
  UPDATE_VISITOR_STATUS: data => data,
  GET_QUEUE: data => data,
  GET_QUEUE_INLINE: data => data,
  ADD_QUEUE: data => data,
  REMOVE_QUEUE: data => data,
  QUEUE_OFFLINE: data => data,
  QUEUE_ONLINE: data => data
});

const visitor = handleActions(
  {
    [getVisitor]: (state, action) => {
      return state.merge({
        visitorList: dealMaxVisitor([], action.payload),
        onlineCount: action.payload?.length ?? 0
      });
    },
    [visitorCome]: (state, action) => {
      if (action.payload?.length > 0) {
        const onlineCount = state.get('onlineCount');
        const cacheVisitorInviteStatus = state.get('cacheVisitorInviteStatus');
        return state.merge({
          visitorList: mergeVisitor(state.get('visitorList'), action.payload, cacheVisitorInviteStatus),
          onlineCount: onlineCount + action.payload.length
        });
      }
      return state;
    },
    [visitorOffline]: (state, action) => {
      if (action.payload?.length > 0) {
        const onlineCount = state.get('onlineCount');
        const trackIds = action.payload.map(val => val.track_id);
        const count = onlineCount - action.payload.length;
        return state.merge({
          visitorList: removeVisitorByTrackId(state, trackIds),
          onlineCount: count >= 0 ? count : 0
        });
      }
      return state;
    },

    [endConversation](state, action) {
      return updateVisitorByTrackId(state, action.payload.track_id, visitor => {
        return {
          ...visitor,
          status: INVITE_STATUS.END_CONVERSE
        };
      });
    },

    [inviteVisitor](state, action) {
      let track_id = action.payload.track_id;
      return updateVisitorByTrackId(state, track_id, visitor => {
        return {
          ...visitor,
          status: INVITE_STATUS.INVITING
        };
      });
    },

    [rejectInvite](state, action) {
      const track_id = action.payload.track_id;

      return updateVisitorByTrackId(state, track_id, visitor => {
        return {
          ...visitor,
          status: INVITE_STATUS.REJECT_INVITE
        };
      });
    },

    [acceptInvite](state, action) {
      return updateVisitorByTrackId(state, action.payload.track_id, visitor => {
        return {
          ...visitor,
          status: INVITE_STATUS.CONVERSE
        };
      });
    },

    [changeInviteStatus](state, action) {
      return updateVisitorInviteStatus(state, action.payload);
    },

    [updateVisitorStatus](state, action) {
      return updateVisitorByTrackId(state, action.payload.track_id, visitor => {
        return {
          ...visitor,
          status: action.payload.status
        };
      });
    },
    [getQueue](state, action) {
      const { payload } = action;
      return state.set('queueList', payload);
    },
    [getQueueInline](state, action) {
      const { payload } = action;
      return state.set('queueInline', payload);
    },
    [addQueue]: (state, action) => {
      const { payload } = action;
      return addOrMergeQueue(state, payload.track_id, queue => {
        return {
          ...payload,
          ...queue
        };
      });
    },
    [removeQueue]: (state, action) => {
      const { payload } = action;
      return removeQueueByTrackId(state, payload);
    },
    [queueOffline]: (state, action) => {
      if (action.payload?.length > 0) {
        const trackIds = action.payload.map(val => val.track_id);
        return updateQueueStatus(state, trackIds, queue => {
          return {
            status: 'offline',
            ...queue
          };
        });
      }
      return state;
    },
    [queueOnline]: (state, action) => {
      if (action.payload?.length > 0) {
        const trackIds = action.payload.map(val => val.track_id);
        return updateQueueStatus(state, trackIds, queue => {
          return {
            status: 'online',
            ...queue
          };
        });
      }
      return state;
    }
  },
  initState
);

export default visitor;
